import React, { useState, useEffect, useRef, useCallback } from "react";
import { browserHistory } from "react-router";
import { IconButton } from "@material-ui/core";
import { Delete, PersonAdd } from "@material-ui/icons";

import ConfirmationModal from "../../shared/ConfirmationModal";
import PaginatedTable from "../../shared/PaginatedTable";
import Toggle from "../../shared/Toggle";

import ChatApi from "../../../services/resources/ChatApi";
import { ApiCallErrorMessageHandler } from "../../../lib/coc-common-scripts";
import { formatFullName } from "../../../lib";
import axios from "axios";
import debounce from "lodash.debounce";

const sortOptions = [
  { id: "name", name: "Name" },
  { id: "chabadHouseName", name: "Chabad House Name" },
  { id: "campusName", name: "Campus Name" },
];

const MemberFilterOptions = {
  Members: "Members",
  NonMembers: "NonMembers",
};

const ActionTypes = {
  Add: "add",
  Remove: "remove",
};

const resultsPerPage = 12;

export function ChatShluchim({ allowEdit }) {
  // shluchim list state
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(sortOptions[0].id);
  const [filters, setFilters] = useState({
    text: "",
    memberFilter: MemberFilterOptions.Members,
  });
  const [shluchim, setShluchim] = useState([]);
  const [totalShluchim, setTotalShluchim] = useState(0);

  // shluchim action state
  const [actionModal, setActionModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionErrorMessage, setActionErrorMessage] = useState("");

  const apiSignalRef = useRef(axios.CancelToken.source());

  useEffect(() => {
    const _apiSignal = apiSignalRef.current;
    return () => _apiSignal.cancel();
  }, []);

  const getShluchim = useCallback(
    async (
      pageNum = 1,
      numResults = resultsPerPage,
      sortByValue = sortBy,
      filtersValue = filters,
    ) => {
      setErrorMessage("");
      setLoading(true);
      setPage(pageNum);
      setSortBy(sortByValue);

      try {
        const { shluchim: shluchimData, totalShluchim: total } =
          await ChatApi.getShluchim(
            pageNum,
            numResults,
            sortByValue,
            filtersValue,
            apiSignalRef.current.token,
          );

        setLoading(false);
        setShluchim(shluchimData);
        setTotalShluchim(total);
      } catch (err) {
        if (!axios.isCancel(err)) {
          setErrorMessage(ApiCallErrorMessageHandler(err));
          setLoading(false);
          setShluchim([]);
          setTotalShluchim(0);
        }
      }
    },
    [filters, sortBy],
  );

  const filterShluchim = useCallback(
    (newFilters) => {
      setFilters(newFilters);
      getShluchim(undefined, undefined, undefined, newFilters);
    },
    [getShluchim],
  );

  const handleAction = useCallback(async () => {
    if (!actionModal) return;

    setActionErrorMessage("");
    setActionLoading(true);

    try {
      if (actionModal.action === ActionTypes.Add) {
        await ChatApi.addChatMember(
          actionModal.shliach.personID,
          apiSignalRef.current.token,
        );
      } else if (actionModal.action === ActionTypes.Remove) {
        await ChatApi.removeChatMember(
          actionModal.shliach.personID,
          apiSignalRef.current.token,
        );
      }

      getShluchim(page);
      setActionModal(null);
      setActionLoading(false);
    } catch (err) {
      if (!axios.isCancel(err)) {
        setActionErrorMessage(ApiCallErrorMessageHandler(err));
        setActionLoading(false);
      }
    }
  }, [actionModal, page, getShluchim]);

  return (
    <div>
      <div className="mb-24">
        <p className="xxl-text fw-500">Shluchim</p>
        <p className="accent-text small-text">
          Shluchim must be associated with an enrolled Chabad House location to
          be added to the chat community. Membership for Shluchim is managed by
          Admin; they are not automatically added when joining an enrolled
          Chabad House.
        </p>
      </div>
      <div className="chat-shluchim-table">
        <PaginatedTable
          filterComponent={
            <Filters initialFilters={filters} applyFilters={filterShluchim} />
          }
          loading={loading}
          loadData={getShluchim}
          page={page}
          records={shluchim}
          renderHeader={() => <Header />}
          renderRow={(shliach, index) => (
            <Row
              allowEdit={allowEdit}
              key={index}
              shliach={shliach}
              onAddMember={() =>
                setActionModal({ action: ActionTypes.Add, shliach })
              }
              onRemoveMember={() =>
                setActionModal({ action: ActionTypes.Remove, shliach })
              }
            />
          )}
          results={resultsPerPage}
          showResultsCount={true}
          sortBy={sortBy}
          sortOptions={sortOptions}
          success={!errorMessage}
          totalCount={totalShluchim}
        />
      </div>

      {!!actionModal && (
        <ConfirmationModal
          cancel={() => {
            setActionModal(null);
            setActionErrorMessage("");
          }}
          confirm={handleAction}
          errorMessage={actionErrorMessage}
          loading={actionLoading}
          message={
            actionModal.action === ActionTypes.Add
              ? `Are you sure you'd like to add ${formatFullName(
                  actionModal.shliach.firstName,
                  actionModal.shliach.lastName,
                )} to the chat community`
              : `Are you sure you'd like to remove ${formatFullName(
                  actionModal.shliach.firstName,
                  actionModal.shliach.lastName,
                )} from the chat community?`
          }
          show={true}
        />
      )}
    </div>
  );
}

function Filters({ applyFilters, initialFilters }) {
  const [text, setText] = useState(initialFilters.text);
  const [memberFilter, setMemberFilter] = useState(initialFilters.memberFilter);

  const debouncedApplyFilters = useRef(debounce(applyFilters, 500));
  const isInitialMount = useRef(true);

  useEffect(() => {
    // run effect when filters are updated - exclude initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    debouncedApplyFilters.current({ text, memberFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const handleMemberFilterChange = (name, value) => {
    setMemberFilter(value);
    applyFilters({ text, memberFilter: value });
  };

  return (
    <div className="flex flex-align-center">
      <div className="search-input">
        <input
          type="text"
          placeholder="Search Shliach name"
          name="text"
          onChange={(event) => setText(event.target.value)}
          value={text}
        />
        <i className="material-icons accent-text-secondary flex flex-align-center">
          search
        </i>
      </div>
      <div className="ml-24">
        <Toggle
          name="memberFilter"
          onChange={handleMemberFilterChange}
          options={[
            { value: MemberFilterOptions.Members, display: "Members" },
            { value: MemberFilterOptions.NonMembers, display: "Non-Members" },
          ]}
          value={memberFilter}
        />
      </div>
    </div>
  );
}

function Header() {
  const columns = ["Name", "Chabad House", "Campus", ""];

  return (
    <div className="chat-shluchim-table-header">
      {columns.map((column, index) => (
        <p className="flex flex-align-center" key={index}>
          {column}
        </p>
      ))}
    </div>
  );
}

function Row({ allowEdit, shliach, onAddMember, onRemoveMember }) {
  const {
    shliachID,
    firstName,
    lastName,
    chabadHouseName,
    campusName,
    isChatMember,
  } = shliach;

  return (
    <div
      className="chat-shluchim-table-row"
      onClick={() => browserHistory.push(`/shluchim/${shliachID}`)}
    >
      <p>{formatFullName(firstName, lastName)}</p>
      <p>{chabadHouseName}</p>
      <p>{campusName}</p>
      <p>
        {isChatMember ? (
          <IconButton
            disabled={!allowEdit}
            onClick={(e) => {
              e.stopPropagation();
              onRemoveMember();
            }}
          >
            <Delete fontSize="large" />
          </IconButton>
        ) : (
          <IconButton
            disabled={!allowEdit}
            onClick={(e) => {
              e.stopPropagation();
              onAddMember();
            }}
          >
            <PersonAdd fontSize="large" />
          </IconButton>
        )}
      </p>
    </div>
  );
}
